import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, FileText, AlertTriangle, Languages } from 'lucide-react';
import TheoryRenderer from '../../components/course/TheoryRenderer';

// LegalPage
// ─────────
// Универсальная страница правового документа.
// Один компонент, контент берётся по slug → i18n-ключ → markdown.
//
// Зачем так: каждый из 7 документов имеет идентичную структуру (Markdown +
// дата обновления + ToC + контакт). Делать 7 одинаковых компонентов было бы
// дублированием. Контент полностью лежит в JSON-локалях (RU+KK), переключение
// языка работает мгновенно без re-mount страницы.

// slug → i18n keys. titleKey используется и в заголовке страницы, и в crumbs.
const DOCS = {
  privacy:           { titleKey: 'legal.privacy',           contentKey: 'legal.privacyContent' },
  'data-consent':    { titleKey: 'legal.dataConsent',       contentKey: 'legal.dataConsentContent' },
  terms:             { titleKey: 'legal.terms',             contentKey: 'legal.termsContent' },
  offer:             { titleKey: 'legal.offer',             contentKey: 'legal.offerContent' },
  // Возвраты на платформе НЕ производятся (см. содержимое). Документ оставляем,
  // чтобы Покупатель видел политику явно, а не «возврата нет → значит наверное
  // можно по обстановке». Чёткая формулировка защищает обе стороны.
  refund:            { titleKey: 'legal.refund',            contentKey: 'legal.refundContent' },
  'instructor-rules':{ titleKey: 'legal.instructorRules',   contentKey: 'legal.instructorRulesContent' },
  'content-policy':  { titleKey: 'legal.contentPolicy',     contentKey: 'legal.contentPolicyContent' },
};

const LegalPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const doc = DOCS[slug];

  if (!doc) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
        <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          {t('errors.notFound')}
        </h1>
        <Link
          to="/settings"
          className="inline-flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          <ArrowLeft className="w-4 h-4" /> {t('common.back')}
        </Link>
      </div>
    );
  }

  const changeLang = (lng) => {
    i18n.changeLanguage(lng);
    try { localStorage.setItem('i18nextLng', lng); } catch { /* ignore */ }
  };

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-5">
      {/* Back + language switch — обычно юзер пришёл из /settings, и хочет
          мгновенно переключить язык документа. */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </button>

        <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-1">
          {[
            { id: 'ru', label: 'Русский' },
            { id: 'kk', label: 'Қазақша' },
          ].map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => changeLang(l.id)}
              className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium transition ${
                i18n.resolvedLanguage === l.id
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              <Languages className="w-3 h-3" />
              {l.label}
            </button>
          ))}
        </div>
      </div>

      {/* Header */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
          {t(doc.titleKey)}
        </h1>
      </div>

      {/* Body. Контент в markdown, рендерим через TheoryRenderer — тот же
          движок что для теории-урока: bold, headings, lists, формулы. */}
      <article className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 sm:p-8">
        <TheoryRenderer content={t(doc.contentKey)} />
      </article>

      <p className="text-center text-xs text-gray-500 dark:text-gray-400">
        {t('legal.contactUs')}: <a href="mailto:support@uaqyt.com" className="text-indigo-600 dark:text-indigo-400 hover:underline">support@uaqyt.com</a>
      </p>
    </div>
  );
};

export default LegalPage;
